// POST /api/jobs/recategorize (design §7.2, §8.1) -- enqueues a recategorize
// job (or, per D2's dedupe rule, attaches to one already `queued`/`running`
// for the caller) and returns the job id the caller polls via the existing
// `useJob`. This hook only starts the job; ReclassifyButton owns the polling
// and the honest-state rendering (design §9.1) -- there is no percentage
// field on `jobs.progress`, ever.
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import { jobStatusSchema } from './useJob';

export const recategorizeScopeSchema = z.enum(['all', 'uncategorized', 'range', 'accounts', 'line_items']);
export type RecategorizeScope = z.infer<typeof recategorizeScopeSchema>;

export const recategorizeRequestSchema = z.object({
  scope: recategorizeScopeSchema,
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  account_ids: z.array(z.string()).optional(),
  line_item_ids: z.array(z.string()).optional(),
});
export type RecategorizeRequest = z.infer<typeof recategorizeRequestSchema>;

export const recategorizeResponseSchema = z.object({
  job_id: z.string(),
  status: jobStatusSchema,
  deduped: z.boolean(),
});
export type RecategorizeResponse = z.infer<typeof recategorizeResponseSchema>;

/**
 * A `deduped: true` response means a run for this caller was already
 * `queued`/`running` -- callers attach to `job_id` exactly as they would for
 * a freshly started run. There is no separate error path for that case.
 */
export function useRecategorize() {
  return useMutation({
    mutationFn: async (input: RecategorizeRequest): Promise<RecategorizeResponse> => {
      const response = await client.post('/api/jobs/recategorize', input);
      return parseEnvelope(recategorizeResponseSchema, response.data).data;
    },
  });
}
